import { useContext, useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, Modal, Stack, TextField, Typography } from '@mui/material';
import { modalStyle } from '../../components/modalStyle';
import { enqueueSnackbar } from 'notistack';
import { equipmentApi } from '../../generated/clients';
import { uploadFile } from '../../utils/api-helpers';
import { CreateEquipment, Equipment } from '../../generated';
import { isAdminUser } from '../../utils/user';
import { ImageCropComponent } from '@/components/images/ImageCropComponent';
import { FileInputButton } from '../../components/FileInput';
import { Photo } from '@mui/icons-material';
import { objectHasId } from '@/utils/create-mode-helper';
import { AppContext } from '@/contexts/app.context';
import { useCreateEquipment, useUpdateEquipment } from '@/hooks/equipment-mutation.hooks';

export interface UpdateEquimpmentModalProps {
  equipment?: Equipment | CreateEquipment;
  setEquipment: (e?: Equipment | CreateEquipment) => void;
  onEquipmentUpdated: (e: Equipment) => void;
}

export const UpdateEquipmentModal = (props: UpdateEquimpmentModalProps) => {
  const { equipment, setEquipment } = props;
  const { state } = useContext(AppContext);
  const createEquipmentMutation = useCreateEquipment();
  const updateEquipmentMutation = useUpdateEquipment();
  const [imageFile, setImageFile] = useState<File | null | undefined>();
  const isEditMode = equipment ? objectHasId(equipment) : false;

  return equipment ? (
    <Modal open={equipment !== undefined} onClose={() => setEquipment()}>
      <Box sx={modalStyle}>
        <Typography variant="h5">{isEditMode ? 'Edit Equipment' : 'Create Equipment'} </Typography>
        <TextField fullWidth value={equipment.name} style={{ marginTop: 30 }} onChange={(e) => setEquipment({ ...equipment, name: e.target.value })} label="Name" />
        <Stack direction={'row'} sx={{ gap: 2, m: 1, justifyItems: 'center' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={equipment.isPublic}
                onChange={() => {
                  setEquipment({ ...equipment, isPublic: !equipment.isPublic ? true : false });
                }}
              />
            }
            label="Public"
            disabled={!isAdminUser(state?.userProfile)}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={equipment.hasWeight}
                onChange={() => {
                  setEquipment({ ...equipment, hasWeight: !equipment.hasWeight });
                }}
              />
            }
            label="Has Weight"
          />
        </Stack>
        <TextField
          sx={{ my: 1 }}
          fullWidth
          value={equipment.description}
          label={'Equipment Description'}
          onChange={(v) => setEquipment({ ...equipment, description: v.target.value })}
          multiline
          rows={4}
        />

        <Box sx={{ m: 1 }}>
          <ImageCropComponent imageFile={imageFile} onImageFileChanged={setImageFile} imageId={equipment.imageId} />
          <Box sx={{ mt: 5, mb: 3, columnGap: 2, display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
            <FileInputButton accept=".png,.jpg,.jpeg" startIcon={<Photo />} onFilePicked={(f) => setImageFile(f)}>
              Change Image
            </FileInputButton>
          </Box>
        </Box>

        <Button
          style={{ marginTop: 10 }}
          onClick={async () => {
            try {
              let fileId = undefined;
              let eq = equipment;
              if (imageFile) {
                fileId = await uploadFile(imageFile);
                eq = { ...eq, imageId: fileId };
              }

              if (objectHasId(eq)) {
                await updateEquipmentMutation.mutateAsync(eq);
                props.onEquipmentUpdated(eq);
                enqueueSnackbar('Equipment updated!');
              } else {
                const e = await createEquipmentMutation.mutateAsync(eq);
                props.onEquipmentUpdated(e);
                enqueueSnackbar('Equipment created!');
              }
            } catch (e) {
              if (e instanceof Error) {
                enqueueSnackbar(JSON.parse(e.message).message, { variant: 'error' });
              }
            } finally {
              setEquipment(undefined);
            }
          }}
        >
          Save{' '}
        </Button>
        <Button style={{ marginTop: 10 }} onClick={() => setEquipment(undefined)}>
          Close
        </Button>
      </Box>
    </Modal>
  ) : (
    <></>
  );
};
